/**
 * PDF Floor Plan Picker — Image-export specialization of DocumentPageAssigner
 *
 * DRR-179: Thin wrapper around DocumentPageAssigner for floor plan extraction.
 * Renders PDF pages as thumbnails, admin clicks to assign pages to floors,
 * pages are exported as PNG blobs via canvas.toBlob().
 *
 * Double-click a thumbnail to open a full-screen preview with crop handles —
 * draw a rectangle around the floor plan area, then assign the cropped region.
 *
 * When backend config is available (via usePageAssignmentConfig), delegates
 * entirely to DocumentPageAssigner with mode=ImageExport.
 * Falls back to standalone behavior if backend is unreachable.
 *
 * Uses react-pdf (^10.3.0) already installed in portal.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Stack,
  Group,
  Paper,
  Text,
  Badge,
  Button,
  SimpleGrid,
  Center,
  Loader,
  Box,
  Modal,
  ActionIcon,
  Tooltip,
  TextInput,
} from '@mantine/core';
import {
  IconCheck,
  IconBuilding,
  IconCrop,
  IconArrowsMaximize,
  IconX,
  IconPolygon,
  IconSquare,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { usePageAssignmentConfig } from '@/hooks/usePageAssignmentConfig';
import { DocumentPageAssigner } from './DocumentPageAssigner';
import type { PageAssignment } from './DocumentPageAssigner';

// Configure worker — use CDN matching react-pdf's bundled pdfjs-dist version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface FloorAssignment {
  /** PDF page number (1-indexed) */
  pageNumber: number;
  /** Floor number assigned by admin */
  floor: number;
  /** Floor label (e.g., "First Floor", "Memory Care Wing") */
  label: string;
  /** PNG blob of the rendered page (or cropped region) */
  pngBlob: Blob;
}

interface PdfFloorPlanPickerProps {
  /** PDF file or blob URL to extract pages from */
  pdfSource: File | string;
  /** Callback when a page is assigned to a floor and exported as PNG */
  onPageAssigned: (assignment: FloorAssignment) => void;
  /** Callback when an assigned page is unassigned */
  onPageUnassigned?: (pageNumber: number) => void;
  /** Already-assigned pages (for showing checkmarks) */
  assignedPages?: Map<number, { floor: number; label: string }>;
  /** Thumbnail width in pixels (default: 280) */
  thumbnailWidth?: number;
}

export function PdfFloorPlanPicker({
  pdfSource,
  onPageAssigned,
  onPageUnassigned,
  assignedPages = new Map(),
  thumbnailWidth = 280,
}: PdfFloorPlanPickerProps) {
  const { config, loading: configLoading } = usePageAssignmentConfig('floor_plans');
  const [floorCounter, setFloorCounter] = useState(assignedPages.size + 1);

  const adaptedAssignedPages = useMemo(() => {
    const map = new Map<number, { categoryId: string; categoryLabel: string }>();
    assignedPages.forEach((val, pageNum) => {
      map.set(pageNum, {
        categoryId: `auto_${val.floor}`,
        categoryLabel: val.label,
      });
    });
    return map;
  }, [assignedPages]);

  const handleAssignment = useCallback(
    (assignment: PageAssignment) => {
      if (!assignment.pngBlob) return;
      const floor = floorCounter;
      onPageAssigned({
        pageNumber: assignment.pageNumber,
        floor,
        label: assignment.categoryLabel,
        pngBlob: assignment.pngBlob,
      });
      setFloorCounter((prev) => prev + 1);
    },
    [floorCounter, onPageAssigned],
  );

  // Always use standalone component — it has the click-to-preview + crop workflow.
  // DocumentPageAssigner is the generic backend-driven path; floor plans need the
  // specialized UX (full-screen preview, crop rectangle, retina rendering).
  return (
    <PdfFloorPlanPickerStandalone
      pdfSource={pdfSource}
      onPageAssigned={onPageAssigned}
      onPageUnassigned={onPageUnassigned}
      assignedPages={assignedPages}
      thumbnailWidth={thumbnailWidth}
    />
  );
}

// ---------------------------------------------------------------------------
// Crop types — polygon replaces rectangle (DRR-179)
// ---------------------------------------------------------------------------

/** A single vertex in normalized 0-1 coordinates */
interface CropVertex {
  x: number;
  y: number;
}

/** Crop shape mode */
type CropMode = 'rectangle' | 'freeform' | 'preset';

/** Preset polygon templates (normalized 0-1, centered at origin — scaled/positioned on apply) */
const PRESET_SHAPES: Record<string, { label: string; vertices: CropVertex[] }> = {
  rectangle: {
    label: 'Rectangle',
    vertices: [
      { x: 0.1, y: 0.1 },
      { x: 0.9, y: 0.1 },
      { x: 0.9, y: 0.9 },
      { x: 0.1, y: 0.9 },
    ],
  },
  lShape: {
    label: 'L-Shape',
    vertices: [
      { x: 0.1, y: 0.1 },
      { x: 0.55, y: 0.1 },
      { x: 0.55, y: 0.5 },
      { x: 0.9, y: 0.5 },
      { x: 0.9, y: 0.9 },
      { x: 0.1, y: 0.9 },
    ],
  },
  tShape: {
    label: 'T-Shape',
    vertices: [
      { x: 0.1, y: 0.1 },
      { x: 0.9, y: 0.1 },
      { x: 0.9, y: 0.4 },
      { x: 0.65, y: 0.4 },
      { x: 0.65, y: 0.9 },
      { x: 0.35, y: 0.9 },
      { x: 0.35, y: 0.4 },
      { x: 0.1, y: 0.4 },
    ],
  },
  uShape: {
    label: 'U-Shape',
    vertices: [
      { x: 0.1, y: 0.1 },
      { x: 0.35, y: 0.1 },
      { x: 0.35, y: 0.6 },
      { x: 0.65, y: 0.6 },
      { x: 0.65, y: 0.1 },
      { x: 0.9, y: 0.1 },
      { x: 0.9, y: 0.9 },
      { x: 0.1, y: 0.9 },
    ],
  },
};

/** Distance between two points in normalized coords */
function vertexDist(a: CropVertex, b: CropVertex): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/** Snap a vertex position to 90-degree angles relative to adjacent vertices.
 *  Hold shift to enable snapping. Returns snapped position if within threshold. */
function snapTo90Degrees(
  pos: CropVertex,
  vertices: CropVertex[],
  vertexIndex: number,
  threshold = 0.03,
): CropVertex {
  if (vertices.length < 2) return pos;

  const prevIdx = (vertexIndex - 1 + vertices.length) % vertices.length;
  const nextIdx = (vertexIndex + 1) % vertices.length;
  const prev = vertices[prevIdx] ?? pos;
  const next = vertices[nextIdx] ?? pos;

  let snappedX = pos.x;
  let snappedY = pos.y;

  // Snap to horizontal/vertical with previous vertex
  if (Math.abs(pos.x - prev.x) < threshold) snappedX = prev.x;
  if (Math.abs(pos.y - prev.y) < threshold) snappedY = prev.y;

  // Snap to horizontal/vertical with next vertex
  if (Math.abs(pos.x - next.x) < threshold) snappedX = next.x;
  if (Math.abs(pos.y - next.y) < threshold) snappedY = next.y;

  return { x: snappedX, y: snappedY };
}

/** Convert polygon vertices to CSS clipPath polygon() string for the dim overlay.
 *  Uses the "hole" technique: outer boundary + inner polygon winding. */
function polygonToClipPath(vertices: CropVertex[]): string {
  if (vertices.length < 3) return 'none';
  const first = vertices[0];
  if (!first) return 'none';
  const outer = '0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%';
  const inner = vertices
    .map((v) => `${v.x * 100}% ${v.y * 100}%`)
    .join(', ');
  // Close inner polygon back to first vertex
  const close = `${first.x * 100}% ${first.y * 100}%`;
  return `polygon(${outer}, ${inner}, ${close})`;
}

/** Compute bounding box of polygon vertices */
function polygonBBox(vertices: CropVertex[]): { x: number; y: number; w: number; h: number } {
  let minX = 1, minY = 1, maxX = 0, maxY = 0;
  for (const v of vertices) {
    if (v.x < minX) minX = v.x;
    if (v.y < minY) minY = v.y;
    if (v.x > maxX) maxX = v.x;
    if (v.y > maxY) maxY = v.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Backward compat: convert old CropRect to polygon
interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function rectToPolygon(r: CropRect): CropVertex[] {
  return [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
}

// ---------------------------------------------------------------------------
// Standalone fallback with double-click → maximize + crop
// ---------------------------------------------------------------------------

function PdfFloorPlanPickerStandalone({
  pdfSource,
  onPageAssigned,
  onPageUnassigned,
  assignedPages = new Map(),
  thumbnailWidth = 280,
}: PdfFloorPlanPickerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<number | null>(null);
  const [nextFloor, setNextFloor] = useState(1);

  // Double-click preview state
  const [previewPage, setPreviewPage] = useState<number | null>(null);
  const [cropVertices, setCropVertices] = useState<CropVertex[]>([]);
  const [cropClosed, setCropClosed] = useState(false);
  const [cropMode, setCropMode] = useState<CropMode>('rectangle');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [draggingVertex, setDraggingVertex] = useState<number | null>(null);
  const [floorLabel, setFloorLabel] = useState('');
  const [shiftHeld, setShiftHeld] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Track shift key for 90-degree snapping
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftHeld(true); };
    const handleKeyUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftHeld(false); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Derived: is the polygon valid for cropping?
  const hasValidCrop = cropClosed && cropVertices.length >= 3;

  // Thumbnail render scale — retina-aware but capped for perf
  const thumbScale = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 2;

  // Preview render scale — higher for architectural detail (4 floors per page needs sharp lines)
  // No `width` prop on preview Page — let PDF render at native resolution * scale,
  // then CSS-scale the canvas down to fit the viewport. This preserves all PDF detail.
  const previewScale = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 2;

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: n }: { numPages: number }) => {
      setNumPages(n);
      setLoading(false);
      setNextFloor(assignedPages.size + 1);
    },
    [assignedPages.size],
  );

  // Export full page as PNG
  const exportFullPage = useCallback(
    async (pageNumber: number): Promise<Blob | null> => {
      const canvasId = `floor-plan-page-${pageNumber}`;
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
      if (!canvas) return null;

      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
          'image/png',
        );
      });
    },
    [],
  );

  // Export polygon-cropped region from the hi-res preview canvas
  const exportCroppedPage = useCallback(
    async (pageNumber: number, vertices: CropVertex[]): Promise<Blob | null> => {
      const canvasId = `floor-plan-preview-${pageNumber}`;
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
      if (!canvas || vertices.length < 3) return null;

      const bbox = polygonBBox(vertices);
      const sw = Math.round(bbox.w * canvas.width);
      const sh = Math.round(bbox.h * canvas.height);
      if (sw < 10 || sh < 10) return null;

      const offscreen = document.createElement('canvas');
      offscreen.width = sw;
      offscreen.height = sh;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return null;

      // Clip to polygon path — pixels outside are transparent
      ctx.beginPath();
      const first = vertices[0];
      if (!first) return null;
      ctx.moveTo(
        (first.x - bbox.x) * canvas.width,
        (first.y - bbox.y) * canvas.height,
      );
      for (let i = 1; i < vertices.length; i++) {
        const v = vertices[i];
        if (!v) continue;
        ctx.lineTo(
          (v.x - bbox.x) * canvas.width,
          (v.y - bbox.y) * canvas.height,
        );
      }
      ctx.closePath();
      ctx.clip();

      // Draw source image offset by bounding box origin
      ctx.drawImage(
        canvas,
        Math.round(bbox.x * canvas.width),
        Math.round(bbox.y * canvas.height),
        sw,
        sh,
        0,
        0,
        sw,
        sh,
      );

      return new Promise<Blob>((resolve, reject) => {
        offscreen.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Crop toBlob failed'))),
          'image/png',
        );
      });
    },
    [],
  );

  // Single click: quick-assign full page
  const handleAssignPage = useCallback(
    async (pageNumber: number) => {
      setExporting(pageNumber);
      try {
        const blob = await exportFullPage(pageNumber);
        if (!blob) return;

        const floor = nextFloor;
        const label = numPages === 1 ? 'Floor Plan' : `Floor ${floor}`;
        onPageAssigned({ pageNumber, floor, label, pngBlob: blob });
        setNextFloor((prev) => prev + 1);
      } finally {
        setExporting(null);
      }
    },
    [nextFloor, numPages, onPageAssigned, exportFullPage],
  );

  // Open maximized preview with crop tool
  const openPreview = useCallback((pageNumber: number) => {
    setPreviewPage(pageNumber);
    setCropVertices([]);
    setCropClosed(false);
    setCropMode('rectangle');
    setIsDragging(false);
    setDragStart(null);
    setDraggingVertex(null);
    setFloorLabel(numPages === 1 ? 'Floor Plan' : `Floor ${nextFloor}`);
  }, [numPages, nextFloor]);

  // Close preview
  const closePreview = useCallback(() => {
    setPreviewPage(null);
    setCropVertices([]);
    setCropClosed(false);
    setIsDragging(false);
    setDragStart(null);
    setDraggingVertex(null);
  }, []);

  // Navigate to previous/next page within preview modal
  const goToPrevPage = useCallback(() => {
    if (previewPage == null || previewPage <= 1) return;
    const newPage = previewPage - 1;
    setPreviewPage(newPage);
    setCropVertices([]);
    setCropClosed(false);
    setFloorLabel(numPages === 1 ? 'Floor Plan' : `Floor ${nextFloor}`);
  }, [previewPage, numPages, nextFloor]);

  const goToNextPage = useCallback(() => {
    if (previewPage == null || previewPage >= numPages) return;
    const newPage = previewPage + 1;
    setPreviewPage(newPage);
    setCropVertices([]);
    setCropClosed(false);
    setFloorLabel(numPages === 1 ? 'Floor Plan' : `Floor ${nextFloor}`);
  }, [previewPage, numPages, nextFloor]);

  // Apply a preset shape
  const applyPreset = useCallback((shapeKey: string) => {
    const preset = PRESET_SHAPES[shapeKey];
    if (!preset) return;
    setCropVertices(preset.vertices.map((v) => ({ ...v })));
    setCropClosed(true);
    setCropMode('preset');
  }, []);

  // Get normalized coords from mouse event
  const getNormCoords = useCallback((e: React.MouseEvent<HTMLDivElement>): CropVertex | null => {
    if (!previewContainerRef.current) return null;
    const rect = previewContainerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  }, []);

  // Find if click is near an existing vertex (for dragging)
  const findNearVertex = useCallback((pos: CropVertex, threshold = 0.02): number | null => {
    for (let i = 0; i < cropVertices.length; i++) {
      const v = cropVertices[i];
      if (v && vertexDist(pos, v) < threshold) return i;
    }
    return null;
  }, [cropVertices]);

  // Mouse down: start rectangle drag, place freeform vertex, or start vertex drag
  const handlePreviewMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Right-click is handled by context menu
      if (e.button === 2) return;
      const pos = getNormCoords(e);
      if (!pos) return;

      // If polygon is closed, check if clicking near a vertex to drag it
      if (cropClosed) {
        const nearIdx = findNearVertex(pos);
        if (nearIdx !== null) {
          setDraggingVertex(nearIdx);
          setIsDragging(true);
          e.preventDefault();
          return;
        }
        // Clicking elsewhere when closed — do nothing (don't restart)
        return;
      }

      if (cropMode === 'rectangle') {
        // Rectangle mode: drag to draw
        setDragStart(pos);
        setCropVertices([]);
        setCropClosed(false);
        setIsDragging(true);
      }
      // Freeform click-to-place is handled in handlePreviewClick
    },
    [cropMode, cropClosed, getNormCoords, findNearVertex],
  );

  const handlePreviewMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      const pos = getNormCoords(e);
      if (!pos) return;

      if (draggingVertex !== null) {
        // Dragging an existing vertex — snap to 90° if shift held
        setCropVertices((prev) => {
          const next = [...prev];
          const snappedPos = shiftHeld
            ? snapTo90Degrees(pos, prev, draggingVertex)
            : pos;
          next[draggingVertex] = snappedPos;
          return next;
        });
        return;
      }

      if (cropMode === 'rectangle' && dragStart) {
        // Live rectangle preview
        const rect: CropRect = {
          x: Math.min(dragStart.x, pos.x),
          y: Math.min(dragStart.y, pos.y),
          w: Math.abs(pos.x - dragStart.x),
          h: Math.abs(pos.y - dragStart.y),
        };
        setCropVertices(rectToPolygon(rect));
      }
    },
    [isDragging, draggingVertex, cropMode, dragStart, getNormCoords, shiftHeld],
  );

  const handlePreviewMouseUp = useCallback(() => {
    if (draggingVertex !== null) {
      setDraggingVertex(null);
      setIsDragging(false);
      return;
    }
    if (cropMode === 'rectangle' && isDragging && cropVertices.length === 4) {
      // Finished drawing rectangle
      setCropClosed(true);
    }
    setIsDragging(false);
  }, [cropMode, isDragging, draggingVertex, cropVertices.length]);

  // Freeform click: place vertex or close polygon
  const handlePreviewClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (cropMode !== 'freeform' || cropClosed) return;
      const pos = getNormCoords(e);
      if (!pos) return;

      // Close polygon if clicking near first vertex (and have ≥3 vertices)
      const firstVertex = cropVertices[0];
      if (cropVertices.length >= 3 && firstVertex && vertexDist(pos, firstVertex) < 0.025) {
        setCropClosed(true);
        return;
      }

      setCropVertices((prev) => [...prev, pos]);
    },
    [cropMode, cropClosed, cropVertices, getNormCoords],
  );

  // Double-click: auto-close polygon in freeform mode
  const handlePreviewDblClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (cropMode !== 'freeform' || cropClosed) return;
      if (cropVertices.length >= 3) {
        setCropClosed(true);
        e.preventDefault();
      }
    },
    [cropMode, cropClosed, cropVertices.length],
  );

  // Right-click: delete vertex
  const handlePreviewContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!cropClosed || cropVertices.length <= 3) return;
      e.preventDefault();
      const pos = getNormCoords(e);
      if (!pos) return;
      const nearIdx = findNearVertex(pos, 0.025);
      if (nearIdx !== null) {
        setCropVertices((prev) => prev.filter((_, i) => i !== nearIdx));
      }
    },
    [cropClosed, cropVertices.length, getNormCoords, findNearVertex],
  );

  // Assign from preview — cropped or full
  const handleAssignFromPreview = useCallback(
    async (useCrop: boolean) => {
      if (previewPage == null) return;
      setExporting(previewPage);
      try {
        let blob: Blob | null = null;
        if (useCrop && hasValidCrop) {
          blob = await exportCroppedPage(previewPage, cropVertices);
        }
        // Fallback to full page if crop failed or wasn't drawn
        if (!blob) {
          blob = await exportFullPage(previewPage);
        }
        if (!blob) return;

        const floor = nextFloor;
        const label = floorLabel.trim() || (numPages === 1 ? 'Floor Plan' : `Floor ${floor}`);
        onPageAssigned({ pageNumber: previewPage, floor, label, pngBlob: blob });
        setNextFloor((prev) => prev + 1);
        closePreview();
      } finally {
        setExporting(null);
      }
    },
    [previewPage, hasValidCrop, cropVertices, nextFloor, numPages, floorLabel, onPageAssigned, exportCroppedPage, exportFullPage, closePreview],
  );

  const fileSource = useMemo(
    () => (pdfSource instanceof File ? URL.createObjectURL(pdfSource) : pdfSource),
    [pdfSource],
  );

  // Compute a reasonable preview width — fill the modal
  const previewWidth = typeof window !== 'undefined' ? Math.min(window.innerWidth - 80, 1200) : 900;

  return (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <IconBuilding size={16} />
          <Text fw={600} size="sm">Floor Plan Pages</Text>
        </Group>
        <Text size="xs" c="dimmed">
          {assignedPages.size} assigned · {numPages} total pages
        </Text>
      </Group>

      <Text size="xs" c="dimmed">
        Click a page to maximize it. Draw a crop shape (rectangle, polygon, or preset) or assign the full page.
      </Text>

      {loading && (
        <Center py="md">
          <Loader size="sm" />
          <Text size="xs" c="dimmed" ml="xs">Loading PDF...</Text>
        </Center>
      )}

      <Document file={fileSource} onLoadSuccess={onDocumentLoadSuccess} loading={null}>
        {/* Thumbnail grid */}
        <SimpleGrid cols={{ base: 2, sm: 3, md: numPages > 6 ? 4 : 3 }} spacing="sm">
          {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
            const assigned = assignedPages.get(pageNum);
            const isExporting = exporting === pageNum;

            return (
              <Paper
                key={pageNum}
                withBorder
                radius="md"
                p="xs"
                style={{
                  borderColor: assigned
                    ? 'var(--mantine-color-green-5)'
                    : 'var(--mantine-color-default-border)',
                  borderWidth: assigned ? 2 : 1,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                }}
                onClick={() => openPreview(pageNum)}
              >
                <Box
                  style={{
                    overflow: 'hidden',
                    borderRadius: 'var(--mantine-radius-sm)',
                    backgroundColor: 'white',
                    position: 'relative',
                    pointerEvents: 'none',
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    width={thumbnailWidth}
                    scale={thumbScale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    canvasRef={(canvas) => {
                      if (canvas) {
                        canvas.id = `floor-plan-page-${pageNum}`;
                        canvas.style.width = `${thumbnailWidth}px`;
                        canvas.style.height = 'auto';
                      }
                    }}
                  />
                  {/* Maximize icon */}
                  <ActionIcon
                    variant="filled"
                    color="dark"
                    size="sm"
                    radius="xl"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      opacity: 0.6,
                      pointerEvents: 'auto',
                    }}
                    onClick={(e) => { e.stopPropagation(); openPreview(pageNum); }}
                  >
                    <IconArrowsMaximize size={12} />
                  </ActionIcon>
                </Box>

                <Group gap="xs" mt="xs" justify="space-between" wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    Page {pageNum}
                  </Text>
                  {assigned ? (
                    <Group gap={4} wrap="nowrap">
                      <Badge size="sm" variant="filled" color="green" leftSection={<IconCheck size={10} />}>
                        {assigned.label}
                      </Badge>
                      {onPageUnassigned && (
                        <Tooltip label="Remove floor assignment">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="red"
                            onClick={(e) => { e.stopPropagation(); onPageUnassigned(pageNum); }}
                          >
                            <IconX size={12} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  ) : (
                    <Button
                      size="compact-xs"
                      variant="light"
                      loading={isExporting}
                      onClick={(e) => { e.stopPropagation(); handleAssignPage(pageNum); }}
                    >
                      Floor {nextFloor}
                    </Button>
                  )}
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      </Document>

      {/* Maximized preview modal — own Document context so the portal works */}
      <Modal
        opened={previewPage != null}
        onClose={closePreview}
        size="auto"
        fullScreen
        title={
          <Group gap="sm">
            <IconBuilding size={18} />
            {/* Page navigation */}
            <Group gap={4}>
              <ActionIcon
                size="sm"
                variant="subtle"
                disabled={previewPage == null || previewPage <= 1}
                onClick={goToPrevPage}
              >
                <IconChevronLeft size={16} />
              </ActionIcon>
              <Text fw={600} style={{ minWidth: 80, textAlign: 'center' }}>
                Page {previewPage} / {numPages}
              </Text>
              <ActionIcon
                size="sm"
                variant="subtle"
                disabled={previewPage == null || previewPage >= numPages}
                onClick={goToNextPage}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            </Group>
            <Text size="sm" c="dimmed">
              {previewPage != null && assignedPages.has(previewPage)
                ? `${assignedPages.get(previewPage)?.label} (assigned)`
                : 'Draw a shape to crop, or assign full page'}
            </Text>
          </Group>
        }
        styles={{
          body: { padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' },
          header: { padding: '8px 16px' },
        }}
      >
        {/* Toolbar */}
        <Group px="md" py="xs" justify="space-between" style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}>
          <Group gap="xs">
            <IconCrop size={16} />
            {/* Crop mode selector */}
            {previewPage != null && !assignedPages.has(previewPage) && (
              <>
                <Tooltip label="Rectangle crop (drag to draw)">
                  <ActionIcon
                    size="sm"
                    variant={cropMode === 'rectangle' ? 'filled' : 'subtle'}
                    color={cropMode === 'rectangle' ? 'blue' : 'gray'}
                    onClick={() => { setCropMode('rectangle'); setCropVertices([]); setCropClosed(false); }}
                  >
                    <IconSquare size={14} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Freeform polygon (click to place vertices)">
                  <ActionIcon
                    size="sm"
                    variant={cropMode === 'freeform' ? 'filled' : 'subtle'}
                    color={cropMode === 'freeform' ? 'blue' : 'gray'}
                    onClick={() => { setCropMode('freeform'); setCropVertices([]); setCropClosed(false); }}
                  >
                    <IconPolygon size={14} />
                  </ActionIcon>
                </Tooltip>
                <Text size="xs" c="dimmed" mx={4}>|</Text>
                {/* Preset shapes */}
                {Object.entries(PRESET_SHAPES).filter(([k]) => k !== 'rectangle').map(([key, shape]) => (
                  <Tooltip key={key} label={`${shape.label} preset`}>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => applyPreset(key)}
                    >
                      {shape.label}
                    </Button>
                  </Tooltip>
                ))}
              </>
            )}
            <Text size="sm" c="dimmed" ml="xs">
              {hasValidCrop
                ? `${cropVertices.length} vertices · ${Math.round(polygonBBox(cropVertices).w * 100)}% × ${Math.round(polygonBBox(cropVertices).h * 100)}% · Hold Shift to snap to 90°`
                : cropMode === 'freeform'
                  ? cropVertices.length > 0
                    ? `${cropVertices.length} vertices — click near first to close, or double-click`
                    : 'Click to place vertices. Double-click or click first vertex to close.'
                  : 'Click and drag to select the floor plan area'}
            </Text>
          </Group>
          <Group gap="xs">
            {previewPage != null && assignedPages.has(previewPage) ? (
              <Badge size="lg" variant="light" color="green" leftSection={<IconCheck size={12} />}>
                {assignedPages.get(previewPage)?.label}
              </Badge>
            ) : (
              <>
                <TextInput
                  size="xs"
                  placeholder="Floor name (e.g., Main Floor, Memory Care Wing)"
                  value={floorLabel}
                  onChange={(e) => setFloorLabel(e.currentTarget.value)}
                  style={{ width: 260 }}
                  styles={{ input: { height: 28, minHeight: 28 } }}
                />
                {hasValidCrop && (
                  <>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => { setCropVertices([]); setCropClosed(false); }}
                      leftSection={<IconX size={14} />}
                    >
                      Clear crop
                    </Button>
                    <Button
                      size="xs"
                      variant="filled"
                      color="green"
                      loading={exporting === previewPage}
                      onClick={() => handleAssignFromPreview(true)}
                      leftSection={<IconCrop size={14} />}
                    >
                      Assign Cropped as {floorLabel || `Floor ${nextFloor}`}
                    </Button>
                  </>
                )}
                <Button
                  size="xs"
                  variant="light"
                  loading={exporting === previewPage}
                  onClick={() => handleAssignFromPreview(false)}
                >
                  Assign Full Page as {floorLabel || `Floor ${nextFloor}`}
                </Button>
              </>
            )}
          </Group>
        </Group>

        {/* Preview canvas + crop overlay — separate Document for portal isolation */}
        <Box
          style={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: 16,
            background: 'var(--mantine-color-gray-1)',
          }}
        >
          {previewPage != null && (
            <Document file={fileSource} loading={null}>
              <Box
                ref={previewContainerRef}
                style={{
                  position: 'relative',
                  cursor: !assignedPages.has(previewPage)
                    ? draggingVertex !== null ? 'grabbing'
                    : cropClosed ? 'default'
                    : 'crosshair'
                    : 'default',
                  userSelect: 'none',
                  lineHeight: 0,
                }}
                onMouseDown={!assignedPages.has(previewPage) ? handlePreviewMouseDown : undefined}
                onMouseMove={!assignedPages.has(previewPage) ? handlePreviewMouseMove : undefined}
                onMouseUp={!assignedPages.has(previewPage) ? handlePreviewMouseUp : undefined}
                onMouseLeave={!assignedPages.has(previewPage) ? handlePreviewMouseUp : undefined}
                onClick={!assignedPages.has(previewPage) ? handlePreviewClick : undefined}
                onDoubleClick={!assignedPages.has(previewPage) ? handlePreviewDblClick : undefined}
                onContextMenu={!assignedPages.has(previewPage) ? handlePreviewContextMenu : undefined}
              >
                <Page
                  pageNumber={previewPage}
                  scale={previewScale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  canvasRef={(canvas) => {
                    if (canvas) {
                      canvas.id = `floor-plan-preview-${previewPage}`;
                      // CSS-scale the hi-res canvas to fit viewport while keeping all native detail
                      canvas.style.maxWidth = `${previewWidth}px`;
                      canvas.style.width = '100%';
                      canvas.style.height = 'auto';
                    }
                  }}
                />

                {/* Polygon crop overlay */}
                {cropVertices.length >= 3 && (
                  <>
                    {/* Dimmed areas outside crop polygon */}
                    <Box
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        pointerEvents: 'none',
                        clipPath: polygonToClipPath(cropVertices),
                      }}
                    />
                    {/* Polygon border via SVG */}
                    <svg
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                      }}
                      viewBox="0 0 1 1"
                      preserveAspectRatio="none"
                    >
                      <polygon
                        points={cropVertices.map((v) => `${v.x},${v.y}`).join(' ')}
                        fill="none"
                        stroke="var(--mantine-color-blue-5)"
                        strokeWidth="0.003"
                        strokeDasharray="0.008 0.005"
                      />
                    </svg>
                  </>
                )}

                {/* In-progress freeform line segments (not yet closed) */}
                {cropMode === 'freeform' && !cropClosed && cropVertices.length >= 1 && (
                  <svg
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                    }}
                    viewBox="0 0 1 1"
                    preserveAspectRatio="none"
                  >
                    <polyline
                      points={cropVertices.map((v) => `${v.x},${v.y}`).join(' ')}
                      fill="none"
                      stroke="var(--mantine-color-blue-5)"
                      strokeWidth="0.003"
                      strokeDasharray="0.008 0.005"
                    />
                  </svg>
                )}

                {/* Vertex handles — draggable circles */}
                {cropVertices.map((v, i) => (
                  <Box
                    key={i}
                    style={{
                      position: 'absolute',
                      left: `${v.x * 100}%`,
                      top: `${v.y * 100}%`,
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: i === 0 && cropMode === 'freeform' && !cropClosed
                        ? 'var(--mantine-color-green-5)'
                        : 'var(--mantine-color-blue-5)',
                      border: '2px solid white',
                      transform: 'translate(-50%, -50%)',
                      cursor: cropClosed ? 'grab' : 'default',
                      pointerEvents: cropClosed ? 'auto' : 'none',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      zIndex: 10,
                    }}
                    onMouseDown={(e) => {
                      if (!cropClosed) return;
                      e.stopPropagation();
                      setDraggingVertex(i);
                      setIsDragging(true);
                    }}
                  />
                ))}

                {/* Size label */}
                {hasValidCrop && (
                  <Badge
                    size="sm"
                    variant="filled"
                    color="blue"
                    style={{
                      position: 'absolute',
                      left: `${polygonBBox(cropVertices).x * 100}%`,
                      top: `${polygonBBox(cropVertices).y * 100}%`,
                      transform: 'translateY(-100%)',
                      pointerEvents: 'none',
                    }}
                  >
                    {cropVertices.length} vertices · {Math.round(polygonBBox(cropVertices).w * 100)}% x {Math.round(polygonBBox(cropVertices).h * 100)}%
                  </Badge>
                )}
              </Box>
            </Document>
          )}
        </Box>
      </Modal>
    </Stack>
  );
}
